'use client'

import { Button } from '@/components/ui/button'
import {
  CurrentOrderQuery,
  useCurrentOrderQuery,
  useCurrentUserQuery,
  useRemoveItemMutation,
  Size,
  Color,
  Condition,
} from '@/api'
import { Trash2 } from 'lucide-react'
import { useRouter } from 'next/navigation'
import CartWhiteIcon from '@/app/_components/icons/CartWhiteIcon'
import { useListingsStore } from '@/stores/useListingsStore'
import { ListingItem } from '@/app/listings/components/Main/ListingItem'

const soldOutStatuses = ['waiting', 'shipped']

export default function Cart() {
  const router = useRouter()
  const { data: { currentUser } = {} } = useCurrentUserQuery()
  const lastListingsUrl = useListingsStore((state) => state.lastListingsUrl)
  const { data: { currentOrder } = {} } = useCurrentOrderQuery({
    variables: {
      brandUuid: process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE ?? '',
    },
    skip: !process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE,
    fetchPolicy: 'cache-and-network',
  })

  const [removeItem] = useRemoveItemMutation()

  const handleRemoveItem = (
    e: React.MouseEvent<SVGSVGElement>,
    listing: NonNullable<
      CurrentOrderQuery['currentOrder']['items']
    >[number]['listing']
  ) => {
    e.stopPropagation()
    e.preventDefault()

    if (!currentOrder) return

    removeItem({
      variables: {
        orderUuid: currentOrder.uuid,
        listingUuid: listing.uuid,
      },
    })
  }

  const handleCheckout = () => {
    if (currentUser) {
      router.push('/checkout/address')
    } else {
      router.push('/checkout/guest')
    }
  }

  const handleContinueShopping = () => {
    if (lastListingsUrl !== '/listings') {
      router.push(lastListingsUrl)
      return
    }

    if (currentUser?.sex) {
      router.push(`/listings?gender=${currentUser.sex}`)
      return
    }

    router.push('/listings')
  }

  const hasSoldOutItem =
    currentOrder?.items?.some((item) =>
      soldOutStatuses.includes(item.listing.status)
    ) ?? false

  return (
    <>
      <div className="container mx-auto justify-center grid py-8 px-5">
        <h1 className="text-center text-[21px] lg:text-3xl mb-6 lg:mb-16 font-semibold">
          カートに入っている製品
        </h1>
        <div className="hidden lg:flex gap-4 border-b text-sm my-5 pb-4">
          <div className="text-center grow-[4]">製品情報</div>
        </div>

        <ul className={`grid gap-5 w-full`}>
          {currentOrder?.items?.map((item) => (
            <li
              key={item.listing.product.uuid}
              className="last-of-type:border-b last-of-type:pb-5 sm:border-b sm:pb-5"
            >
              <div className="relative lg:w-[788px]">
                <ListingItem
                  uuid={item.listing.uuid || ''}
                  title={item.listing.product.productMaster.name}
                  listingPrice={item.listing.price || 0}
                  image={item.listing.images?.[0]}
                  images={item.listing.images || undefined}
                  size={item.listing.size as Size}
                  condition={item.listing.condition as Condition}
                  color={item.listing.product.color as Color}
                  sex={item.listing.product.productMaster?.sex}
                  horizontal={true}
                />
                <div className="absolute top-1/2 -translate-y-1/2 right-2">
                  <Trash2
                    className="cursor-pointer text-[#565656] w-4 h-4 hover:text-red-600"
                    onClick={(e) => handleRemoveItem(e, item.listing)}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
        <dl className="grid justify-end mt-5 gap-1 md:gap-4 text-sm text-[#2A2930] mb-12 sm:mb-14">
          <div className="flex gap-24 sm:gap-16">
            <dt className="flex-1">小計(税込)</dt>
            <dd className="text-right">
              ¥{currentOrder?.itemTotalPrice?.toLocaleString()}
            </dd>
          </div>
          <div className="flex gap-24 sm:gap-16">
            <dt className="flex-1">送料(税込)</dt>
            <dd className="text-right">
              ¥{currentOrder?.shippingTotalPrice?.toLocaleString()}
            </dd>
          </div>
          <div className="flex gap-24 sm:gap-16">
            <dt className="flex-1">製品合計(税込)</dt>
            <dd className="text-right">
              ¥{currentOrder?.totalPrice?.toLocaleString()}
            </dd>
          </div>
        </dl>
        <div className="flex flex-col md:flex-row items-center justify-center my-6 gap-4">
          <Button
            type="button"
            className="w-64 h-12 rounded-none border border-black bg-white text-black hover:bg-gray-100 order-2 md:order-1"
            onClick={handleContinueShopping}
          >
            ショッピングを続ける
          </Button>
          <Button
            type="submit"
            className="w-64 h-12 order-1 md:order-2 rounded-none bg-black"
            onClick={handleCheckout}
            disabled={hasSoldOutItem}
          >
            <CartWhiteIcon />
            ご購入手続きに進む
          </Button>
        </div>
      </div>
    </>
  )
}
